/**
 * Version 3 to 4: the five guns take their real names.
 *
 * Every stack names its item by id and a rolled weapon names its gun by id, and both sets of ids
 * changed. Walks the three places a stack can sit and maps what it recognises, leaving anything
 * else exactly as it was: `readProfile` validates once the chain has run.
 */

import type { SaveMigration, SaveRecord } from '@/save/migrationContract';

const ITEM_IDS: Readonly<Record<string, string>> = {
  pistolSidearm: 'glock17',
  submachineGun: 'mp5',
  assaultRifle: 'm4a1',
  pumpShotgun: 'remington870',
  boltActionRifle: 'remington700',
};

const WEAPON_IDS: Readonly<Record<string, string>> = {
  pistol: 'glock17',
  smg: 'mp5',
  rifle: 'm4a1',
  shotgun: 'remington870',
  sniper: 'remington700',
};

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function renamed(value: unknown, table: Readonly<Record<string, string>>): unknown {
  return typeof value === 'string' ? (table[value] ?? value) : value;
}

function renameStack(stack: unknown): unknown {
  if (!isRecord(stack)) return stack;
  const rolled = isRecord(stack.rolled)
    ? { ...stack.rolled, weaponId: renamed(stack.rolled.weaponId, WEAPON_IDS) }
    : stack.rolled;
  return { ...stack, defId: renamed(stack.defId, ITEM_IDS), rolled };
}

function renameContainer(container: unknown): unknown {
  if (!isRecord(container) || !Array.isArray(container.stacks)) return container;
  return { ...container, stacks: container.stacks.map(renameStack) };
}

function renameEquipment(equipment: unknown): unknown {
  if (!isRecord(equipment)) return equipment;
  return Object.fromEntries(
    Object.entries(equipment).map(([slot, stack]) => [slot, renameStack(stack)]),
  );
}

export const RENAME_WEAPON_IDS: SaveMigration = {
  from: 3,
  apply(record: SaveRecord): SaveRecord {
    if (!isRecord(record.inventory)) return record;
    const inventory = record.inventory;
    return {
      ...record,
      inventory: {
        ...inventory,
        carried: renameContainer(inventory.carried),
        stash: renameContainer(inventory.stash),
        equipment: renameEquipment(inventory.equipment),
      },
    };
  },
};
